// 2026-08-18 — per-country, per-household cost-of-living dataset.
//
// Loaded from /data/cost_of_living.json (static, produced by research agents).
// Replaces the fabricated `metrics.cost.monthly_eur_family` /
// `metrics.cost.rent_capital_eur` fields (see research/metrics-fabrication-audit.md)
// with five sourced household baskets per country — see
// docs/data/COST_OF_LIVING_SCHEMA.md for the full contract.
//
// Used by: scoreCost() in scoring.ts, computeNeutralScores() in
// neutralScores.ts, buildCountryNarrative() in country-narrative.ts.
//
// Design: `getHouseholdTotal()` is a pure function — takes the data file and
// returns a number or null. Mirrors scoreVisaPathway() / scoreTaxIntent():
// null means "not loaded / not covered" and the caller falls back to the
// legacy metrics.cost field. Six research agents are populating the file as
// of this writing, so this file will not exist for a while — every caller
// MUST tolerate `file` being undefined.

import { useQuery } from '@tanstack/react-query';
import type { HouseholdBucket } from './derive';

export type { HouseholdBucket };

// ─── Schema types (docs/data/COST_OF_LIVING_SCHEMA.md) ─────────────────────

export interface HouseholdCostBreakdown {
  rent: number;
  groceries: number;
  utilities: number;
  transport: number;
  childcare_education: number;
  health: number;
  other: number;
  /** Sum of every field above, including education. */
  total: number;
  /**
   * `total` minus `childcare_education`, and the number scoring reads.
   *
   * `total` is not comparable across countries, because
   * `childcare_education` does not measure the same thing everywhere. In 25
   * countries it is provision available locally to a resident — municipal
   * daycare, public schooling. In AE, ID, MY, SG, TH and VN it is
   * international-school tuition, because a child who does not speak the
   * language of instruction has no realistic public route, and the UAE has
   * no free public schooling for expatriate children at all.
   *
   * Both are correct answers, to different questions. Every figure in the
   * file is real and sourced. What breaks is the comparison: with
   * international fees inside the total, Bangkok's family of four reads as
   * more expensive than Vienna's, while Bangkok's rent is EUR 964 against
   * Vienna's EUR 1,785. Excluding education, the family-to-single ratio
   * lands between 1.84 and 2.44 for all 31 countries with no outlier, which
   * is what a consistent basket looks like.
   *
   * So ranking uses this field. `childcare_education_basis` on the country
   * says which question `total` answers, and belongs next to it wherever it
   * is shown.
   */
  total_excl_education?: number;
}

/**
 * Which question a country's `total` answers. `local` where
 * `childcare_education` is provision available to a resident locally;
 * `international` where it is international-school tuition because no
 * realistic public route exists for a foreign resident's child.
 */
export type SchoolingBasis = 'local' | 'international';

export interface CostOfLivingSource {
  field: string;
  name: string;
  url?: string;
  tier: 'A' | 'B';
  retrieved: string;
}

export interface CostOfLivingAnchor {
  value: number | null;
  tier: 'A' | 'B' | null;
  url?: string | null;
  note?: string;
}

export interface CountryCostOfLiving {
  city: string;
  cityRole?: string;
  currency_local?: string;
  fx_to_eur?: number;
  fx_date?: string;
  /**
   * Which question this country's `total` answers: `local` where
   * `childcare_education` is provision available to a resident locally,
   * `international` where it is international-school tuition because no
   * realistic public route exists for a foreign resident's child. Show it
   * wherever `total` is shown; rank on `total_excl_education` instead.
   */
  childcare_education_basis?: SchoolingBasis;
  households: Partial<Record<HouseholdBucket, HouseholdCostBreakdown>>;
  sources?: CostOfLivingSource[];
  anchors?: {
    minimum_wage_month?: CostOfLivingAnchor;
    median_net_wage_month?: CostOfLivingAnchor;
  };
  confidence: 'A' | 'B';
  last_verified?: string;
  notes?: string;
}

export interface CostOfLivingFile {
  _meta: {
    generated: string;
    currency: string;
    basis: string;
    excludes: string;
    includes: string;
    sourcePolicy?: string;
    /** Points at the i18n key for the reader-facing disclaimer — 'quiz.cost.disclaimer'. */
    disclaimer_i18n_key: string;
  };
  countries: Record<string, CountryCostOfLiving>;
}

// ─── Data hook ───────────────────────────────────────────────────────────

export function useCostOfLiving() {
  return useQuery({
    queryKey: ['cost-of-living'],
    queryFn: async (): Promise<CostOfLivingFile> => {
      const res = await fetch('/data/cost_of_living.json');
      if (!res.ok) throw new Error(`cost_of_living.json: ${res.status}`);
      return res.json();
    },
    staleTime: 7 * 24 * 60 * 60 * 1000, // 1 week
    gcTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
}

// ─── Lookup helper ─────────────────────────────────────────────────────────

/**
 * Monthly total (EUR) for one household bucket in one country.
 *
 * Returns null when the file hasn't loaded yet, the country isn't covered,
 * or that specific bucket is missing for the country — callers fall back to
 * the legacy `metrics.cost.monthly_eur_family` field in that case.
 */
export function getHouseholdTotal(
  file: CostOfLivingFile | undefined,
  countryCode: string,
  bucket: HouseholdBucket,
): number | null {
  if (!file) return null;
  const entry = file.countries[countryCode.toUpperCase()];
  if (!entry) return null;
  const household = entry.households[bucket];
  if (!household) return null;
  // Education is excluded so countries can be ranked against each other; see
  // the field's own comment for why `total` cannot be.
  return household.total_excl_education ?? household.total;
}

/**
 * The household a surface shows when the reader has not told us theirs.
 *
 * `single` rather than a family basket, and deliberately so. The field this
 * dataset replaces defaulted the other way: it showed every reader one number
 * labelled "family of ~4", which was answering the wrong question for four
 * readers in five before you even reach the fact that it was fabricated.
 *
 * A single-person basket is the one figure that needs no assumption about who
 * the reader lives with. Every surface that renders it must name the household
 * next to it, which is what `countries.metrics.costHousehold.*` is for. A cost
 * figure whose household is not stated is the defect, not the number.
 */
export const DEFAULT_DISPLAY_BUCKET: HouseholdBucket = 'single';

export interface DisplayCost {
  /** Monthly EUR excluding childcare and schooling. */
  amount: number;
  bucket: HouseholdBucket;
  /** The city the basket was priced in. Never show the amount without it. */
  city: string;
  /** Childcare or schooling for this household, kept separate from `amount`. */
  childcareEducation: number | null;
  /** What that childcare figure measures: local provision, or international fees. */
  basis: SchoolingBasis | null;
}

/**
 * Everything a surface needs to render one country's cost honestly.
 *
 * Returns null when the file has not loaded or the country is not covered,
 * so callers render nothing rather than falling back to the fabricated
 * `metrics.cost.monthly_eur_family`. Showing a placeholder dash is the right
 * answer here: the old field's whole problem was that a number nobody sourced
 * looked exactly like a number somebody had.
 */
export function getDisplayCost(
  file: CostOfLivingFile | undefined,
  countryCode: string,
  bucket: HouseholdBucket = DEFAULT_DISPLAY_BUCKET,
): DisplayCost | null {
  if (!file) return null;
  const entry = file.countries[countryCode.toUpperCase()];
  if (!entry) return null;
  const household = entry.households[bucket];
  if (!household) return null;
  const amount = household.total_excl_education ?? household.total;
  if (typeof amount !== 'number') return null;
  return {
    amount,
    bucket,
    city: entry.city,
    childcareEducation:
      typeof household.childcare_education === 'number' ? household.childcare_education : null,
    basis: entry.childcare_education_basis ?? null,
  };
}
